<script setup lang="ts">
import type { FavoriteGame } from '~/types/favorite-game';
import PerfilOficialSwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';

interface Props {
  alwaysShow?: boolean; // Força exibição mesmo sem jogos (para renderização via API)
  hideHeader?: boolean; // Esconde o header quando renderizado dentro de tabs
  scaleFactor?: number; // Fator de escala para o tamanho dos cards (padrão: 1)
  title?: string; // Título vindo da API (data.name)
}

const props = withDefaults(defineProps<Props>(), {
  alwaysShow: false,
  hideHeader: false,
  scaleFactor: 1,
  title: 'Jogos Favoritos',
});

const { getFavoriteGames, hasFavorites, onFavoriteGamesUpdate } =
  useFavoriteGames();

const { isAuthenticated } = storeToRefs(useAuthStore());
const { getResponsiveThumbnailSrc } = useResponsiveThumbnail();

const favoriteGames = ref<FavoriteGame[]>([]);

const loadFavoriteGames = () => {
  if (isAuthenticated.value && hasFavorites()) {
    favoriteGames.value = getFavoriteGames();
  } else {
    favoriteGames.value = [];
  }
};

// Registra listener para atualizações automáticas
let unsubscribe: (() => void) | null = null;

onMounted(() => {
  unsubscribe = onFavoriteGamesUpdate(() => {
    loadFavoriteGames();
  });
  loadFavoriteGames();
});

watch(
  isAuthenticated,
  (newValue) => {
    if (!newValue) {
      favoriteGames.value = [];
    } else {
      loadFavoriteGames();
    }
  },
  { immediate: true },
);

const shouldShow = computed(() => {
  // Se alwaysShow é true, mostra sempre que estiver autenticado (independente de ter jogos)
  if (props.alwaysShow) {
    return isAuthenticated.value;
  }

  // Comportamento padrão: só mostra se tiver jogos
  return isAuthenticated.value && favoriteGames.value.length > 0;
});

// Atualizar scroll quando o componente se tornar visível
watch(shouldShow, (newValue) => {
  if (newValue) {
    nextTick(() => {
      checkScrollFades();
    });
  }
});

// Sempre exibir 10 cards
const displayItems = computed(() => {
  const items = [...favoriteGames.value];

  const emptyCards = 10 - items.length;

  // Preencher com cards vazios até completar 10
  for (let i = 0; i < emptyCards; i++) {
    items.push({} as FavoriteGame);
  }

  return items.slice(0, 10);
});

const isEmptySlot = (game: FavoriteGame) => !game.game_name || !game.slug;

// Dimensões base dos cards
const BASE_CARD_WIDTH_DESKTOP = 136; // px
const BASE_CARD_WIDTH_MOBILE = 87.25; // px
const BASE_FONT_SIZE_DESKTOP = 11; // px
const BASE_FONT_SIZE_MOBILE = 10; // px

// Dimensões calculadas com base no scaleFactor
const cardWidthDesktop = computed(
  () => BASE_CARD_WIDTH_DESKTOP * props.scaleFactor,
);

const cardWidthMobile = computed(
  () => BASE_CARD_WIDTH_MOBILE * props.scaleFactor,
);

const fontSizeDesktop = computed(
  () => BASE_FONT_SIZE_DESKTOP * props.scaleFactor,
);

const fontSizeMobile = computed(
  () => BASE_FONT_SIZE_MOBILE * props.scaleFactor,
);

// Detectar se é o primeiro card vazio
const isFirstEmptyCard = (index: number) => {
  const game = displayItems.value[index];
  const firstEmptyPosition = favoriteGames.value.length;

  return game && isEmptySlot(game) && index === firstEmptyPosition;
};

const { validateGameUrl } = useGamesWinners();

const gamesGrid = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const dragged = ref(false);
const showFadeLeft = ref(false);
const showFadeRight = ref(false);

const currentScrollLeft = ref(0);
const currentScrollWidth = ref(0);
const currentClientWidth = ref(0);

const updateScrollMetrics = () => {
  if (gamesGrid.value) {
    currentScrollLeft.value = gamesGrid.value.scrollLeft;
    currentScrollWidth.value = gamesGrid.value.scrollWidth;
    currentClientWidth.value = gamesGrid.value.clientWidth;
  }
};

const scrollNext = () => {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(updateScrollMetrics, 100);
  }
};

const scrollPrev = () => {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    setTimeout(updateScrollMetrics, 100);
  }
};

const swiperControls = {
  scrollNext,
  scrollPrev,
  currentScrollLeft,
  currentScrollWidth,
  currentClientWidth,
};

const onMouseDown = (e: MouseEvent) => {
  if (!gamesGrid.value) return;
  isDragging.value = true;
  startX.value = e.pageX - gamesGrid.value.offsetLeft;
  scrollLeft.value = gamesGrid.value.scrollLeft;
  gamesGrid.value.style.cursor = 'grabbing';
  gamesGrid.value.style.userSelect = 'none';
  dragged.value = false;
};

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !gamesGrid.value) return;
  e.preventDefault();

  const x = e.pageX - gamesGrid.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  gamesGrid.value.scrollLeft = scrollLeft.value - walk;
  updateScrollMetrics();
};

const onMouseUp = () => {
  if (!gamesGrid.value) return;
  isDragging.value = false;
  gamesGrid.value.style.cursor = 'grab';
  gamesGrid.value.style.userSelect = 'auto';
};

const onMouseLeave = onMouseUp;

const checkScrollFades = () => {
  if (!gamesGrid.value) return;

  updateScrollMetrics();

  const hasOverflow = gamesGrid.value.scrollWidth > gamesGrid.value.clientWidth;

  showFadeLeft.value = hasOverflow && gamesGrid.value.scrollLeft > 0;
  showFadeRight.value =
    hasOverflow &&
    gamesGrid.value.scrollLeft + gamesGrid.value.clientWidth <
      gamesGrid.value.scrollWidth;
};

const handleGameClick = (event: MouseEvent) => {
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;
  }
};

onMounted(() => {
  loadFavoriteGames();

  nextTick(() => {
    checkScrollFades();
  });

  gamesGrid.value?.addEventListener('scroll', checkScrollFades);
  window.addEventListener('resize', checkScrollFades);
});

onBeforeUnmount(() => {
  // Remove listener de atualizações
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }

  // Remove event listeners apenas se existirem
  if (gamesGrid.value) {
    gamesGrid.value.removeEventListener('scroll', checkScrollFades);
  }
  window.removeEventListener('resize', checkScrollFades);
});

// Expor swiperControls para o componente pai
defineExpose({
  swiperControls,
});
</script>

<template>
  <div
    v-if="shouldShow"
    class="favorite-games-list"
  >
    <PerfilOficialSwiperHeader
      v-if="!hideHeader"
      class="favorite-games-header"
      hide-navigation
      icon-path="home/favorite"
      :swiper-ref="swiperControls"
      :title="title"
    />

    <div
      ref="gamesGrid"
      class="favorite-games-list__grid"
      :style="{
        '--card-width-desktop': `${cardWidthDesktop}px`,
        '--card-width-mobile': `${cardWidthMobile}px`,
        '--font-size-desktop': `${fontSizeDesktop}px`,
        '--font-size-mobile': `${fontSizeMobile}px`,
      }"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <template
        v-for="(game, index) in displayItems"
        :key="game.game_id || `empty-${index}`"
      >
        <!-- Card vazio - primeiro -->
        <div
          v-if="isEmptySlot(game) && isFirstEmptyCard(index)"
          class="favorite-games-list__card favorite-games-list__card--empty-first"
        >
          <div class="favorite-games-list__empty-content-first">
            <div
              class="self-stretch inline-flex justify-end items-start gap-[2.75px]"
            ></div>
            <div class="favorite-games-list__empty-text">
              Seus jogos favoritos aparecerão aqui!
            </div>
            <div
              class="self-stretch inline-flex justify-end items-start gap-[2.75px]"
            ></div>
          </div>
        </div>

        <!-- Cards vazios - demais -->
        <div
          v-else-if="isEmptySlot(game)"
          class="favorite-games-list__card favorite-games-list__card--empty"
        >
          <div class="favorite-games-list__empty-content">
            <NuxtIcon
              alt="Aposta Tudo"
              class="favorite-games-list__empty-logo"
              :filled="true"
              name="apostatudo/empty-card-bg-icon"
            />
          </div>
        </div>

        <!-- Card com jogo -->
        <NuxtLink
          v-else
          class="favorite-games-list__card"
          :class="{
            dragging: dragged,
            'favorite-games-list__card--no-image':
              !game.image || game.image === '0',
          }"
          draggable="false"
          :to="validateGameUrl(game.slug)"
          @click="handleGameClick"
        >
          <template v-if="game.image && game.image !== '0'">
            <img
              :alt="game.game_name"
              class="favorite-games-list__image"
              draggable="false"
              :fetchpriority="index < 8 ? 'high' : undefined"
              :loading="index < 8 ? 'eager' : 'lazy'"
              :src="getResponsiveThumbnailSrc(game.image)"
            />
          </template>
          <template v-else>
            <div class="favorite-games-list__name-fallback">
              <span>{{ game.game_name }}</span>
            </div>
          </template>

          <!-- Favorite Button no canto superior direito -->
          <div
            v-if="game.game_id"
            class="favorite-games-list__favorite-button"
            @click.prevent.stop
          >
            <CasinoFavoriteButton
              :game-id="game.game_id"
              :hide-when-not-favorited="true"
              :show-feedback="false"
              size="small"
            />
          </div>
        </NuxtLink>
      </template>
    </div>
  </div>
</template>

<style scoped>
.favorite-games-list {
  width: 100%;
  margin-bottom: 10px;
}

.favorite-games-list__grid {
  display: flex;
  flex-direction: row;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
  min-height: auto;
}

.favorite-games-list__grid::-webkit-scrollbar {
  display: none;
}

.favorite-games-list__card {
  position: relative;
  width: var(--card-width-desktop);
  aspect-ratio: 1 / 1.3;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #0f1724;
  flex-shrink: 0;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.favorite-games-list__favorite-button {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 50;
}

.favorite-games-list__card--empty-first {
  background: #111827;
  border-radius: 4px;
  border: 0.82px dashed rgba(255, 215, 0, 0.3);
  cursor: default;
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 3.44px;
}

.favorite-games-list__empty-content-first {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 8px;
}

.favorite-games-list__empty-text {
  text-align: center;
  color: rgba(255, 215, 0, 0.7);
  font-size: var(--font-size-desktop);
  font-weight: 400;
  font-family: 'Plus Jakarta Sans', sans-serif;
  line-height: 1.4;
  padding: 0 10px;
}

.favorite-games-list__card--empty {
  background: #0f1724;
  border-radius: 4px;
  border: 0.82px dashed rgba(255, 215, 0, 0.2);
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: center;
}

.favorite-games-list__empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 0.3;
}

.favorite-games-list__empty-logo {
  width: 100%;
  height: 100%;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
}

.favorite-games-list__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 4px;
}

.favorite-games-list__card.dragging {
  pointer-events: none;
}

.favorite-games-list__card--no-image {
  background: linear-gradient(180deg, #1e2939 0%, #0d1520 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
}

.favorite-games-list__card--no-image::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 4px;
  padding: 2px;
  background: linear-gradient(135deg, #ffd700 0%, #ffaa00 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.favorite-games-list__name-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px 12px;
  text-align: center;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 215, 0, 0.08) 0%,
    transparent 70%
  );
}

.favorite-games-list__name-fallback span {
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
  letter-spacing: 0.3px;
}

@media (max-width: 768px) {
  .favorite-games-list__card {
    width: var(--card-width-mobile);
  }

  .favorite-games-list__grid {
    min-height: auto;
  }

  .favorite-games-list__empty-logo {
    width: 65%;
    height: 65%;
    font-size: 42px;
  }

  .favorite-games-list__empty-text {
    font-size: var(--font-size-mobile);
    padding: 0 6px;
    line-height: 1.3;
  }
}

@media (max-width: 414px) {
  .favorite-games-list__grid {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}

@media (min-width: 768px) {
  .favorite-games-list {
    margin-bottom: 20px;
  }
}
</style>
